import React from "react";
import { Chip } from "@mui/material";

export const BRAND_BLUE = "#1766A2";
export const BRAND_BLUE_LIGHT = "#2196F3";

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export const formatDate = (value) => (value ? String(value).slice(0, 10) : "—");

// The backend error can arrive as a JSON array of plain business-readable
// reasons (e.g. ["Amount ... exceeds remaining balance ... on loan ..."]), a
// plain string, a JSON-encoded string, or — for unexpected server errors — a
// { Error: "System.Exception: ...." } / Postgres stack-trace wrapper. Unwrap
// all of these down to one readable line (multiple reasons are joined).
export const parseErrorMessage = (error) => {
  try {
    const errorObj = typeof error === "string" ? JSON.parse(error) : error;
    if (Array.isArray(errorObj)) return errorObj.map(String).join("; ");
    if (typeof errorObj === "string") return errorObj;
    if (errorObj?.error) return errorObj.error;
    if (errorObj?.Error) {
      const messageText = errorObj.Error.match(/MessageText:\s*(.*?)(?:\r?\n|$)/);
      if (messageText?.[1]) return messageText[1].trim();
      return errorObj.Error.match(/System\.Exception: (.*?)\./)?.[1] || errorObj.Error;
    }
    return JSON.stringify(errorObj);
  } catch {
    return String(error);
  }
};

export const LIEN_STATUS_COLOR = {
  PENDING: "warning",
  REGISTERED: "success",
  DISCHARGED: "default",
};

export const ASSET_STATUS_COLOR = {
  DRAFT: "default",
  PENDING_VERIF: "warning",
  PENDING_REVIEW: "warning",
  VERIFIED: "info",
  APPROVED: "success",
  ACTIVE: "success",
  REPOSSESSED: "error",
  DISPOSED: "secondary",
  REJECTED: "error",
};

export const StatusChip = ({ status, colorMap }) => (
  <Chip
    label={String(status || "UNKNOWN").replace(/_/g, " ")}
    size="small"
    color={colorMap[status] || "default"}
    sx={{ fontWeight: 700, fontSize: "0.68rem", borderRadius: "5px" }}
  />
);

export const lienColumns = [
  { field: "LienId", headerName: "Lien ID", width: 90 },
  { field: "CustomerName", headerName: "Customer", flex: 1.1, minWidth: 160 },
  { field: "CustomerPhone", headerName: "Phone", width: 130 },
  { field: "AssetTypeName", headerName: "Asset Type", flex: 1, minWidth: 140 },
  { field: "AssetSubTypeName", headerName: "Sub Type", flex: 0.9, minWidth: 120 },
  { field: "RegistryName", headerName: "Registry", flex: 1.2, minWidth: 180 },
  {
    field: "LienStatus",
    headerName: "Status",
    width: 130,
    renderCell: (params) => <StatusChip status={params.value} colorMap={LIEN_STATUS_COLOR} />,
  },
  {
    field: "LienReferenceNo",
    headerName: "Reference No.",
    width: 160,
    valueGetter: (params) => params.row.LienReferenceNo || "—",
  },
  {
    field: "LienRegisteredOn",
    headerName: "Registered On",
    width: 130,
    valueGetter: (params) => formatDate(params.row.LienRegisteredOn),
  },
];

export const assetColumns = [
  { field: "Id", headerName: "Asset ID", width: 90 },
  { field: "CustomerName", headerName: "Customer", flex: 1.1, minWidth: 160 },
  { field: "CustomerPhone", headerName: "Phone", width: 130 },
  { field: "AssetTypeName", headerName: "Asset Type", flex: 1, minWidth: 140 },
  { field: "SubTypeName", headerName: "Sub Type", flex: 0.9, minWidth: 120 },
  {
    field: "IndicativeValue",
    headerName: "Indicative Value",
    width: 160,
    valueGetter: (params) => formatCurrency(params.row.IndicativeValue),
  },
  {
    field: "CurrentFSV",
    headerName: "Forced Sale Value",
    width: 160,
    valueGetter: (params) => formatCurrency(params.row.CurrentFSV),
  },
  {
    field: "AssetStatus",
    headerName: "Status",
    width: 140,
    renderCell: (params) => <StatusChip status={params.value} colorMap={ASSET_STATUS_COLOR} />,
  },
];

export const exportToCsv = (rows, columns, filename) => {
  if (!rows.length) return;
  const exportable = columns.filter((c) => c.field !== "actions");
  const headers = exportable.map((c) => c.headerName || c.field);
  const csvRows = [headers.join(",")];

  rows.forEach((row) => {
    const values = exportable.map((col) => {
      const raw = col.valueGetter ? col.valueGetter({ row }) : row[col.field];
      const cell = raw === null || raw === undefined ? "" : String(raw).replace(/"/g, '""');
      return `"${cell}"`;
    });
    csvRows.push(values.join(","));
  });

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
